import { MatrizEvidencia } from "./matriz-riesgo.model";

/**
 * Evaluaciones IA Solser.
 *
 * Los terceros cuyas herramientas de IA usa SOLSER (ChatGPT, Claude,
 * Copilot...): un cuestionario de 13 preguntas en 3 dominios, un
 * expediente con la evidencia y un dictamen firmado.
 *
 * La regla (15/09/2026): los 3 dominios pesan lo mismo (33.3); con todas
 * las preguntas respondidas y 80 o más, CUMPLE. El expediente no suma
 * puntos: en Sí cumple, el archivo de evidencia es obligatorio para aprobar.
 *
 * El RESULTADO lo calcula siempre el servidor (EvalProveedorCalculo). La
 * pantalla lo pinta, no lo recalcula: así la regla es una sola.
 */

export type RespuestaEval = "CUMPLE" | "NO_CUMPLE";
export type RiesgoEval = "SIN_EVALUAR" | "BAJO" | "MEDIO" | "ALTO" | "CRITICO";
export type DictamenEval = "APROBADO" | "RECHAZADO";
export type EstatusEval = "ABIERTA" | "FIRMADA";

/** El origen con el que los archivos de un documento van al almacén de evidencias. */
export const ORIGEN_EVAL_PROVEEDOR_DOC = "EVAL_PROVEEDOR_DOC";

export const RIESGO_TEXTO: Record<RiesgoEval, string> = {
  SIN_EVALUAR: "Sin evaluar",
  BAJO: "Bajo",
  MEDIO: "Medio",
  ALTO: "Alto",
  CRITICO: "Crítico",
};

export const DICTAMEN_TEXTO: Record<DictamenEval, string> = {
  APROBADO: "Aprobado",
  RECHAZADO: "No aprobado",
};

/* ---------------- El catálogo (vive en el servidor) ---------------- */

export interface EvalCriterio {
  /** SEG.1, DATOS.3... La llave con la que se guarda la respuesta. */
  clave: string;
  dominio: string;
  /** "Pregunta 1" dentro de su dominio. */
  numero: number;
  texto: string;
}

export interface EvalDominio {
  id: string;
  nombre: string;
  /** Porcentaje del total; hoy 33.3 cada uno. */
  peso: number;
  descripcion: string;
  criterios: EvalCriterio[];
}

export interface EvalDocumentoCat {
  clave: string;
  nombre: string;
  /** La clave de la pregunta que lo pide. */
  criterio: string;
}

export interface EvalCatalogo {
  version: string;
  /** Desde cuántos puntos cumple (80). */
  umbralCumple: number;
  dominios: EvalDominio[];
  documentos: EvalDocumentoCat[];
}

/* ---------------- Lo que dice un expediente ---------------- */

export interface EvalPuntajeDominio {
  id: string;
  nombre: string;
  peso: number;
  total: number;
  hechos: number;
  /** Cuántas de sus preguntas están en Sí cumple. */
  cumplen: number;
  /** 0-100 con un decimal: Sí cumple entre las preguntas del dominio (sin respuestas, 0). */
  score: number | null;
  /** Lo que aporta al total: peso × score / 100, con un decimal. */
  aporte: number | null;
}

export interface EvalResultado {
  /** 0-100 con un decimal. Sólo el cuestionario. */
  total: number;
  avance: number;
  respondidos: number;
  totalCriterios: number;
  faltan: number;
  concluida: boolean;
  enNoCumple: number;
  /** Desde cuántos puntos cumple. */
  umbral: number;
  /** Todas respondidas y total de umbral o más. */
  puedeAprobar: boolean;
  riesgo: RiesgoEval;
  sugerido: DictamenEval | null;
  vigenciaSugerida: number | null;
  dominios: EvalPuntajeDominio[];
}

export interface EvalRespuesta {
  idEvalProveedor?: number;
  clave: string;
  respuesta?: RespuestaEval | null;
  observacion?: string | null;
  usuario?: string;
  fecha?: string;
}

export interface EvalDocumento {
  idEvalDoc: number;
  idEvalProveedor?: number;
  claveDoc: string;
  referencia?: string | null;
  usuario?: string;
  fecha?: string;
  archivos?: MatrizEvidencia[];
}

export interface EvalProveedor {
  idEvalProveedor?: number;
  nombre?: string;
  casoUso?: string | null;
  razonSocial?: string | null;
  servicio?: string | null;
  planModalidad?: string | null;
  /** yyyy-MM-dd */
  fechaEvaluacion?: string | null;
  estatus?: EstatusEval;
  dictamen?: DictamenEval | null;
  vigenciaMeses?: number | null;
  puntajeFirma?: number | null;
  riesgoFirma?: RiesgoEval | null;
  idUsuarioFirma?: number | null;
  firmo?: string | null;
  fechaFirma?: string | null;
  /** PNG en base64. Sólo en el detalle de una firmada. */
  firmaDigital?: string | null;
  proximaEvaluacion?: string | null;
  versionCatalogo?: string;
  idUsuarioAlta?: number;
  alta?: string;
  fechaAlta?: string;
  fechaUltimaAfectacion?: string;
  /** Sólo en el detalle. */
  respuestas?: EvalRespuesta[];
  /** Sólo en el detalle. */
  documentos?: EvalDocumento[];
  resultado?: EvalResultado;
}

export interface EvalHistorial {
  idHistorial: number;
  idEvalProveedor?: number;
  accion: string;
  detalle?: string;
  idUsuario?: number;
  usuario?: string;
  fecha?: string;
}

/* ---------------- Request / Response ---------------- */

export interface EvalProveedorReq {
  idEvalProveedor?: number;
  evaluacion?: EvalProveedor;
  clave?: string;
  respuesta?: RespuestaEval | null;
  observacion?: string;
  claveDoc?: string;
  referencia?: string;
  dictamen?: DictamenEval;
  /** La firma digital trazada (PNG en base64), al firmar. */
  firma?: string;
  motivo?: string;
  /** El PIN para eliminar un expediente creado por error. */
  pin?: string;
}

export interface EvalProveedorRes {
  evaluaciones?: EvalProveedor[];
  evaluacion?: EvalProveedor;
  historial?: EvalHistorial[];
  catalogo?: EvalCatalogo;
  idGenerado?: number;
  codigoError?: string;
  mensajeError?: string;
}
